import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import { QUERY_KEYS } from 'consts/queryKeys';
import { useCustomToast } from 'hooks';
import { deleteOccupancyLite, deleteUnitOccupancy, upsertOccupancy } from 'modules/_common/api';

export const useMutationUpsertOccupancy = (id?: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { successToast, errorToast } = useCustomToast();

  const { mutateAsync: upsertOccupancyMutation, isLoading: isLoadingMutation } = useMutation(
    upsertOccupancy,
    {
      onSuccess: response => {
        //backend can return 200 OK, but with errors (overbooking)

        if (response.data.errors === null) {
          const successMessage =
            id === 'empty' ? 'Uspješno ste unijeli zauzeće' : 'Uspješno ste ažurirali zauzeće';
          successToast(successMessage);
        } else {
          const errorMessages = response.data.errors.map((error: any) => error.message);
          errorToast(errorMessages.join(', '));
        }
        queryClient.invalidateQueries(QUERY_KEYS.LITE_RENTER_CALENDAR_RESERVATIONS);
        queryClient.invalidateQueries(QUERY_KEYS.LITE_RESERVATIONS);
        queryClient.invalidateQueries(QUERY_KEYS.GUEST_OCCUPANCIES);
        queryClient.invalidateQueries(QUERY_KEYS.LITE_RESERVATION_DETAILS);
        queryClient.invalidateQueries(QUERY_KEYS.RENTER_ACTIVE_PRICE_LIST);
        queryClient.invalidateQueries(QUERY_KEYS.UNITS_CALENDAR_RESERVATIONS);
        queryClient.invalidateQueries(QUERY_KEYS.LITE_UNITS_OCCUPANCIES);
        queryClient.invalidateQueries(QUERY_KEYS.LINE_CALENDAR_RESERVATIONS);
      },

      onError: (err: any) => {
        let errorMessage = '';

        if (err.response?.data?.Items?.['eRROR-VALIDACIJA-OVERBOOK']) {
          errorMessage = t('Overbooking reservations');
        } else if (err.response?.data?.ErrorDescription === 'Ugovori') {
          errorMessage = t(
            'Accommodation with allotment contract - allowed number of days for entry exceeded',
          );
        } else if (err.response?.data?.items?.['dateFrom']) {
          errorMessage = t('Date from must be before date to');
        } else if (err?.response?.data?.ErrorDescription?.startsWith('No price list defined')) {
          errorMessage = t('noPriceListDefinedOccupancyError');
        } else if (
          err?.response?.data?.ErrorDescription ===
          'Number of days defined by allotment contract would be exceeded!'
        ) {
          errorMessage = t('maxNumberOfDaysOccupancyError');
        }

        if (errorMessage) {
          errorToast(errorMessage);
        } else {
          errorToast();
        }
      },
    },
  );

  const { mutateAsync: deleteOccupancy, isLoading: isLoadingDeleteOccupancy } = useMutation(
    deleteOccupancyLite,
    {
      onSuccess: () => {
        successToast(t('The occupation has been successfully deleted'));
        queryClient.invalidateQueries(QUERY_KEYS.LITE_RENTER_CALENDAR_RESERVATIONS);
        queryClient.invalidateQueries(QUERY_KEYS.LITE_RESERVATIONS);
        queryClient.invalidateQueries(QUERY_KEYS.GUEST_OCCUPANCIES);
        queryClient.invalidateQueries(QUERY_KEYS.LITE_RESERVATION_DETAILS);
        queryClient.invalidateQueries(QUERY_KEYS.RENTER_ACTIVE_PRICE_LIST);
        queryClient.invalidateQueries(QUERY_KEYS.UNITS_CALENDAR_RESERVATIONS);
        queryClient.invalidateQueries(QUERY_KEYS.LITE_UNITS_OCCUPANCIES);
        queryClient.invalidateQueries(QUERY_KEYS.LINE_CALENDAR_RESERVATIONS);
      },
      onError: (err: any) => {
        const errorMessage = err?.response?.data?.ErrorDescription;

        if (errorMessage) {
          errorToast(t(errorMessage));
        } else {
          errorToast();
        }
      },
    },
  );

  return { upsertOccupancyMutation, deleteOccupancy, isLoadingMutation, isLoadingDeleteOccupancy };
};
